<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { GreenbowLogo } from '@greenbow/ui'
import StatusCard from '@/components/StatusCard.vue'
import { parseLang, parseStatus } from '@/lib/params'
import { copyFor, countdownLabel } from '@/i18n'

const search = typeof window === 'undefined' ? '' : window.location.search
const navigatorLanguage = typeof navigator === 'undefined' ? null : navigator.language

const status = parseStatus(search)
const lang = parseLang(search, navigatorLanguage)
const copy = copyFor(lang, status)

if (typeof document !== 'undefined') {
  document.documentElement.lang = lang
  document.title = `${copy.title} — Greenbow`
}

// Skip auto-close in Vite dev (so the page is previewable) and when
// ?preview=1 is set (so docs/stakeholder links don't close themselves).
const isPreview = import.meta.env.DEV || new URLSearchParams(search).get('preview') === '1'

// Auto-close only on confirmed outcomes — on "unknown" the user needs to
// stay and read the support copy.
const shouldAutoClose = !isPreview && (status === 'success' || status === 'cancel')

const AUTO_CLOSE_SECONDS = 5
const secondsLeft = ref(AUTO_CLOSE_SECONDS)

const countdownText = computed(() =>
  shouldAutoClose && secondsLeft.value > 0 ? countdownLabel(lang, secondsLeft.value) : null,
)

let intervalId: ReturnType<typeof setInterval> | null = null
let timeoutId: ReturnType<typeof setTimeout> | null = null

// Best-effort auto-close. Only works when the window was opened via
// window.open() or has a single history entry — typically fails silently
// in in-app browsers and iOS Safari. The visible content (including the
// countdown reaching 0) is the fallback.
onMounted(() => {
  if (typeof window === 'undefined' || !shouldAutoClose) return

  intervalId = setInterval(() => {
    secondsLeft.value = Math.max(0, secondsLeft.value - 1)
  }, 1000)

  timeoutId = setTimeout(() => {
    try {
      window.close()
    } catch {
      // Ignore — fallback is the rendered page.
    }
  }, AUTO_CLOSE_SECONDS * 1000)
})

onUnmounted(() => {
  if (intervalId !== null) clearInterval(intervalId)
  if (timeoutId !== null) clearTimeout(timeoutId)
})
</script>

<template>
  <main class="checkout-return">
    <GreenbowLogo class="checkout-return__logo" :width="160" />
    <StatusCard :variant="status" :title="copy.title" :body="copy.body" />
    <p v-if="countdownText" class="checkout-return__countdown" aria-live="polite">
      {{ countdownText }}
    </p>
  </main>
</template>

<style lang="scss" scoped>
.checkout-return {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: space(6);
  min-height: 100dvh;
  padding: space(6);

  &__logo {
    width: clamp(140px, 30vw, 180px);
  }

  &__countdown {
    margin: 0;
    color: color('grayblue-400');
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
  }
}
</style>
